import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { KeyRound, ArrowLeft, MailCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      // Endpoint intentionally always returns 200 so this catch is mostly network-only.
      toast.error("Could not send reset email — try again");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mb-6" data-testid="back-to-login">
          <ArrowLeft size={12} /> Back to sign in
        </Link>
        <div className="border border-border rounded-lg bg-card p-8">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound size={16} className="text-primary" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Password reset</div>
          </div>
          {sent ? (
            <div data-testid="reset-sent-panel">
              <div className="flex items-center gap-2 text-primary mb-3">
                <MailCheck size={18} /><h1 className="heading text-2xl">Check your email</h1>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                If <span className="font-medium text-foreground">{email}</span> is on file, we've sent a link that lets you set a new password. The link is good for 60 minutes.
              </p>
              <p className="text-xs text-muted-foreground mt-4">
                Didn't get it? Check spam, or try a different email address.
              </p>
              <div className="mt-6 flex gap-2">
                <Button variant="outline" onClick={() => { setSent(false); setEmail(""); }} data-testid="reset-try-another">Try another email</Button>
                <Link to="/login"><Button>Back to sign in</Button></Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h1 className="heading text-2xl mb-2">Forgot your password?</h1>
              <p className="text-sm text-muted-foreground mb-6">Enter the email on your account. We'll send you a one-time link to choose a new password.</p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={email}
                         onChange={(e) => setEmail(e.target.value)}
                         data-testid="forgot-email-input" />
                </div>
                <Button type="submit" className="w-full" disabled={busy || !email} data-testid="forgot-submit-btn">
                  {busy ? "Sending…" : "Send reset link"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
